import { isNil, isUndefined } from 'lodash-es';

import { ConfigReducer as SessionState } from '@/store/slices/session';

import { CONFIGURATOR_DEFAULT_CURRENCY, CONFIGURATOR_DEFAULT_LOCALE } from '../constants';
import {
  CampaignItem,
  CampaignItemType,
  ConfiguratorSessionState,
  PriceMap,
  PricingCosts,
  TextTranslations,
} from '../types';
import { getActiveCampaign, getGamificationItems } from './engagement';

export function isDesignSessionDataEmpty(sessionState: ConfiguratorSessionState = {}): boolean {
  const { siteSeedId, siteSkinId, artStyleId, tokens } = sessionState;

  return [siteSeedId, siteSkinId, artStyleId, tokens].some(isUndefined);
}

export function isEngagementSessionDataEmpty(
  sessionState: ConfiguratorSessionState = {},
  campaignItems: CampaignItem[] = [],
): boolean {
  const sessionGamificationItems = getGamificationItems(sessionState.items);
  const campaignGamificationItems = getGamificationItems(campaignItems);

  return sessionGamificationItems.length < campaignGamificationItems.length;
}

export function calculateDiscount(totalCost: number, discountedCost: number): TextTranslations | undefined {
  const discountAmount = totalCost - discountedCost;
  const percentage = (discountAmount / totalCost) * 100;

  if (discountAmount === 0 || percentage === 0) {
    return;
  }

  return {
    [CONFIGURATOR_DEFAULT_LOCALE]: Math.round(percentage) + '%',
  };
}

export function splitPrices(session: SessionState, currency: string = CONFIGURATOR_DEFAULT_CURRENCY): PriceMap {
  const allCampaigns = [...session.campaigns, ...session.recommendedCampaigns];
  // Look up by campaignId directly so price always tracks the selected campaign,
  // without requiring a valid availability_window (which getActiveCampaign enforces).
  const campaignId = session.data.configuration_data?.campaignId;
  const campaign = campaignId
    ? allCampaigns.find((c) => String(c.id) === String(campaignId))
    : getActiveCampaign(allCampaigns, session.data.configuration_data);
  // Display currency follows the selected market, overriding the campaign's own currency_code.
  const currency_code = currency;
  const { price_evaluation } = campaign ?? {};
  const { discountedPrice = 0, basePrice = 0 } = price_evaluation ?? {};
  const hasDiscount = discountedPrice > 0 && discountedPrice < basePrice;

  const baseItemsPrice: PricingCosts = { currency: currency_code, totalCost: 0, discount: undefined, cost: 0 };
  const itemsPrice: PricingCosts = { currency: currency_code, totalCost: 0, discount: undefined, cost: 0 };

  session.data.configuration_data?.items?.forEach((item) => {
    const campaignItem = campaign?.items?.find(
      (campaignItem) => String(campaignItem.id) === String(item.item_id) && campaignItem.type === item.type,
    );
    const campaignTier = campaignItem?.tiers?.find(
      (campaignTier) => String(campaignTier.id) === String(item.tier_id),
    );

    // Exclude only a DISABLED FEATURE (`item.enabled === false`) from the price — matching the
    // public `/pricing` API the website uses. A TIER's `enabled` flag gates individual
    // selectability in the à-la-carte features step, NOT pack inclusion: a pack can default an
    // enabled feature to a tier that isn't individually selectable (e.g. Edge → AirTimer Ultra),
    // and that price IS part of the pack. Do NOT filter on `campaignTier.enabled` here.
    if (campaignItem?.enabled === false) {
      return;
    }

    const originalCost = campaignTier?.base_price ?? 0;
    let actualCost = originalCost;

    if (hasDiscount && !isNil(campaignTier?.discounted_price)) {
      actualCost = campaignTier.discounted_price;
    }

    const addCosts = (target: { totalCost: number; cost: number }) => {
      target.totalCost += originalCost;
      target.cost += actualCost;
    };

    if (item.type === CampaignItemType.BASE_ITEM) {
      addCosts(baseItemsPrice);

      return;
    }

    if (item.type === CampaignItemType.GAMIFICATION_ITEM) {
      addCosts(itemsPrice);

      return;
    }
  });

  // Campaign-level one-time fees (e.g. the "Setup" fee) carry their own
  // per-currency cost / discounted_cost rather than tier prices, so they aren't
  // picked up by the configuration_data.items loop above. Fold them into the
  // base-items total so the billing section reflects the setup fee + its discount.
  const now = new Date().toISOString();
  const pickCost = (c: any): number =>
    parseFloat(c?.[currency_code] ?? c?.[CONFIGURATOR_DEFAULT_CURRENCY] ?? (Object.values(c ?? {})[0] as string) ?? '0') || 0;
  (campaign as any)?.base_items?.forEach((baseItem: any) => {
    if (baseItem?.is_active === false) return;
    const original = pickCost(baseItem?.cost);
    const disc = baseItem?.discounted_cost;
    const discActive =
      !!disc?.active && (!disc?.start_date || disc.start_date <= now) && (!disc?.end_date || now <= disc.end_date);
    baseItemsPrice.totalCost += original;
    baseItemsPrice.cost += discActive ? pickCost(disc?.cost) : original;
  });

  // Prefer the backend's DIFFERENTIATED price_evaluation (setup vs features) when present — it is
  // authoritative, so use it verbatim instead of the FE item-sum above (no summing / subtracting).
  // `match` gates the discount: false → no discount, use the base figure. Falls back to the sums
  // above for older data that doesn't carry the breakdown.
  if (price_evaluation?.setupPrice != null || price_evaluation?.featuresPrice != null) {
    const pe = price_evaluation;
    baseItemsPrice.totalCost = pe.setupPrice ?? 0;
    baseItemsPrice.cost = pe.match ? pe.discountedSetupPrice ?? pe.setupPrice ?? 0 : pe.setupPrice ?? 0;
    itemsPrice.totalCost = pe.featuresPrice ?? 0;
    itemsPrice.cost = pe.match ? pe.discountedFeaturesPrice ?? pe.featuresPrice ?? 0 : pe.featuresPrice ?? 0;
  }

  return {
    price: {
      currency: currency_code,
      totalCost: basePrice,
      discount: hasDiscount ? calculateDiscount(basePrice, discountedPrice) : undefined,
      cost: hasDiscount ? discountedPrice : basePrice,
    },
    baseItemsPrice: {
      ...baseItemsPrice,
      // Discount is intrinsic to the base items (e.g. the setup fee's own
      // discounted_cost), so derive it from their own totals, not the campaign's.
      discount: calculateDiscount(baseItemsPrice.totalCost, baseItemsPrice.cost),
    },
    itemsPrice: {
      ...itemsPrice,
      discount: hasDiscount ? calculateDiscount(itemsPrice.totalCost, itemsPrice.cost) : undefined,
    },
  };
}
